import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/lib/store'
import { NotificationSound } from './NotificationSound'

interface Notification {
  id: string
  title: string
  content: string
  type: 'system' | 'order' | 'chat' | 'application'
  read: boolean
  created_at: string
  metadata: Record<string, any>
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  hasNewNotification: boolean
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  setHasNewNotification: (value: boolean) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function NotificationProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasNewNotification, setHasNewNotification] = useState(false)
  const [playSound, setPlaySound] = useState(false)

  useEffect(() => {
    if (user) {
      fetchNotifications()
      
      // Set up real-time subscription
      const channel = supabase
        .channel('notifications-channel')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            // Add the new notification to the list
            const newNotification = payload.new as Notification
            setNotifications(prev => [newNotification, ...prev])
            setUnreadCount(prev => prev + 1)
            setHasNewNotification(true)
            setPlaySound(true)
            
            // Show browser notification if supported
            showBrowserNotification(newNotification)
          }
        )
        .subscribe()

      // Clean up subscription on unmount
      return () => {
        channel.unsubscribe()
      }
    }
  }, [user])

  const fetchNotifications = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      
      setNotifications(data || [])
      
      // Count unread notifications
      const unread = data?.filter(n => !n.read).length || 0
      setUnreadCount(unread)
    } catch (err) {
      console.error('Error fetching notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (id: string) => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id)

      if (error) throw error

      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err) {
      console.error('Error marking notification as read:', err)
    }
  }

  const markAllAsRead = async () => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false)

      if (error) throw error

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
      setHasNewNotification(false)
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
    }
  }

  const showBrowserNotification = (notification: Notification) => {
    // Check if browser notifications are supported and permission is granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('AEM Marketplace', {
        body: notification.title,
        icon: '/favicon.ico'
      })
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission()
    }
  }

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        hasNewNotification,
        markAsRead,
        markAllAsRead,
        setHasNewNotification
      }}
    >
      {children}
      <NotificationSound 
        play={playSound} 
        onEnded={() => setPlaySound(false)} 
      />
    </NotificationContext.Provider>
  )
}

const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}